import {
  NAVIGATE_DETAIL_DESTINATIONS,
  NAVIGATE_LIST_DESTINATIONS,
  type NavigateDestination,
} from "../model/constants"
import type { NavigateActionArgs } from "../model/types"

/**
 * Route mapping for Sofia's navigate destinations and cited entities: the
 * destination → org-relative path table the FE owns (sofia-agent validates the
 * destination contract, this module resolves it), plus the deep links for
 * entities Sofia cites inline in a reply. Split out of `utils/actions.ts`,
 * which owns proposal validation and labeling.
 */

export function isNavigateListDestination(value: string): boolean {
  return (NAVIGATE_LIST_DESTINATIONS as readonly string[]).includes(value)
}

export function isNavigateDetailDestination(value: string): boolean {
  return (NAVIGATE_DETAIL_DESTINATIONS as readonly string[]).includes(value)
}

export function isNavigateDestination(
  value: string,
): value is NavigateDestination {
  return isNavigateListDestination(value) || isNavigateDetailDestination(value)
}

/** Human label per destination, used in titles and the "Took you to X" receipt. */
const NAVIGATE_DESTINATION_LABELS: Record<NavigateDestination, string> = {
  home: "Home",
  investigations: "Investigations",
  dashboards: "Dashboards",
  metrics: "Metrics",
  reports: "Reports",
  alerts: "Alerts",
  alert_create: "New alert",
  alert: "alert",
  alert_edit: "alert editor",
  agents: "Agents",
  agent_create: "New Agent",
  personas: "Personas",
  test_sets: "Test Sets",
  templates: "Templates",
  scheduled: "Scheduled",
  simulated_conversations: "Simulated",
  uploaded_conversations: "Uploaded",
  simulations: "Simulated",
  runs: "Simulated",
  conversations: "Uploaded",
  trace_search: "Trace Search",
  human_review: "Human Review",
  simulate: "Simulate",
  settings: "Settings",
  test_set: "test set",
  run: "run",
  report: "report",
  persona: "persona",
  metric: "metric",
  agent: "agent",
  template: "template",
  simulated_conversation: "simulated conversation",
  uploaded_conversation: "uploaded conversation",
  conversation: "uploaded conversation",
  simulation: "simulated conversation",
  simulation_trace: "simulation trace",
  human_review_project: "Human Review project",
  investigation: "investigation finding",
  dashboard: "dashboard",
}

export function navigateDestinationLabel(destination: string): string {
  return isNavigateDestination(destination)
    ? NAVIGATE_DESTINATION_LABELS[destination]
    : destination
}

/**
 * The relative path (within an org) a navigate destination maps to. Built from
 * the canonical sidebar config (`modules/navigation/config/siteNavigation.ts`)
 * and the `[slug]` page routes. The executor prepends `/${orgSlug}` via
 * `orgPath`.
 */
export function navigateRelativePath(args: NavigateActionArgs): string {
  const { destination, resource_id } = args
  switch (destination) {
    case "home":
      return "/home"
    case "investigations":
      // Sofia's Investigations page lives under Home, beside the chat rail.
      return "/home/investigations"
    case "test_sets":
      return "/test-sets"
    case "runs":
    case "simulated_conversations":
      return "/simulated"
    case "personas":
      return "/personas"
    case "metrics":
      return "/metrics"
    case "agents":
      return "/agents"
    case "agent_create":
      return "/agents/create"
    case "simulate":
      return "/simulate"
    case "dashboards":
      return "/dashboard"
    case "reports":
      return "/reports"
    case "alerts":
      return "/alerts"
    case "alert_create":
      return "/alerts/create"
    case "alert":
      return `/alerts/${encodeURIComponent(resource_id ?? "")}`
    case "alert_edit":
      return `/alerts/${encodeURIComponent(resource_id ?? "")}/edit`
    case "scheduled":
      return "/scheduled"
    case "simulations":
      return "/simulated"
    case "conversations":
    case "uploaded_conversations":
      return "/uploaded"
    case "trace_search":
      // The org-wide trace SEARCH page (always safe to open). A single
      // simulation's trace view is the `simulation_trace` detail destination.
      return "/traces"
    case "human_review":
      return "/review/projects"
    case "settings":
      return "/settings"
    case "templates":
      return "/templates"
    case "template":
      return `/templates/${encodeURIComponent(resource_id ?? "")}`
    case "test_set":
      return `/test-sets/${encodeURIComponent(resource_id ?? "")}`
    case "run":
      return `/simulated/runs/${encodeURIComponent(resource_id ?? "")}`
    case "report":
      return `/reports/${encodeURIComponent(resource_id ?? "")}`
    case "agent":
      return `/agents/${encodeURIComponent(resource_id ?? "")}`
    case "persona":
      return `/personas/${encodeURIComponent(resource_id ?? "")}`
    case "metric":
      return `/metrics/${encodeURIComponent(resource_id ?? "")}`
    case "conversation":
    case "uploaded_conversation":
      return `/uploaded/results/${encodeURIComponent(resource_id ?? "")}`
    case "simulation":
    case "simulated_conversation":
      return `/simulated/results/${encodeURIComponent(resource_id ?? "")}`
    case "simulation_trace":
      // One simulation's captured trace view (the external trace detail viewer,
      // /traces/{simulation_output_id}). Sofia only proposes this when traces
      // were actually sent for the simulation (charter gate), so this route is
      // never reached for a simulation with no trace data.
      return `/traces/${encodeURIComponent(resource_id ?? "")}`
    case "investigation":
      // One finding on the Investigations page. The page reads `finding_id` and
      // scroll-focuses that row (InvestigationHistoryRoute), so this is the
      // same deep link the Slack briefing posts.
      return `/home/investigations?finding_id=${encodeURIComponent(resource_id ?? "")}`
    case "dashboard":
      // One dashboard by id — the board Sofia just built with `build_dashboard`.
      return `/dashboard/${encodeURIComponent(resource_id ?? "")}`
    case "human_review_project":
      if (resource_id && args.human_review_view) {
        // The executor persists the normalized state before asking this mapper for the final route.
        return `/review/projects/${encodeURIComponent(resource_id)}/annotations`
      }
      return resource_id
        ? `/review/projects/${encodeURIComponent(resource_id)}`
        : "/review/projects"
  }
}

/**
 * Entity kinds Sofia can CITE inline in a reply and the FE can turn into a
 * clickable deep link.
 * Kept deliberately small so a bare id in prose is only linked when Sofia tags
 * it with an entity kind (see `parseCoviEntityRef`), never guessed.
 */
export const COVI_ENTITY_KINDS = [
  "alert",
  "simulated_conversation",
  "uploaded_conversation",
  "simulation",
  "conversation",
  "run",
  "metric",
] as const

export type CoviEntityKind = (typeof COVI_ENTITY_KINDS)[number]

export function isCoviEntityKind(value: string): value is CoviEntityKind {
  return (COVI_ENTITY_KINDS as readonly string[]).includes(value)
}

/**
 * A single cited entity reference Sofia emits inside its markdown. Sofia tags a
 * cited id with its kind so the FE can link it reliably instead of guessing at
 * opaque ids in prose because typed tool-result ids are sufficient for
 * reliable linking. `turn` is an optional 0-based transcript turn to anchor
 * on (turn-anchored deep links, where the destination page honors it).
 */
export interface CoviEntityRef {
  kind: CoviEntityKind
  id: string
  turn?: number
}

/**
 * The relative path (within an org) a CITED entity id maps to. Reuses
 * `navigateRelativePath` as the source of truth for the matching detail routes:
 *
 * - `simulation` → `/simulated/results/{id}` (simulated conversation detail)
 * - `conversation` → `/uploaded/results/{id}` (uploaded conversation detail)
 * - `metric` → `/metrics/{id}` (metric detail page)
 *
 * NOTE: the `conversation` route is the route that must resolve reliably;
 * we still emit the link here so the two land together. A `turn` is carried as a
 * `?turn=` query param (turn-anchored where the page supports it) — the same
 * pattern the metric deep link uses to pass anchor state through the URL.
 */
export function coviEntityRelativePath(ref: CoviEntityRef): string {
  const base = (() => {
    switch (ref.kind) {
      case "alert":
        return navigateRelativePath({
          destination: "alert",
          resource_id: ref.id,
        })
      case "run":
        return navigateRelativePath({ destination: "run", resource_id: ref.id })
      case "simulation":
      case "simulated_conversation":
        return navigateRelativePath({
          destination: "simulated_conversation",
          resource_id: ref.id,
        })
      case "conversation":
      case "uploaded_conversation":
        return navigateRelativePath({
          destination: "uploaded_conversation",
          resource_id: ref.id,
        })
      case "metric":
        return navigateRelativePath({
          destination: "metric",
          resource_id: ref.id,
        })
    }
  })()
  return ref.turn !== undefined && Number.isInteger(ref.turn) && ref.turn >= 0
    ? `${base}?turn=${ref.turn}`
    : base
}
